import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import './assets/global.css';

// Public
import Home                 from './pages/public/Home';
import { NewsList, NewsDetail } from './pages/public/News';
import Publications         from './pages/public/Publications';
import SimplePublicPage     from './pages/public/SimplePublicPage';
import Contact              from './pages/public/Contact';

// Admin
import AdminLogin           from './pages/admin/AdminLogin';
import AdminDashboard       from './pages/admin/AdminDashboard';
import { AdminNewsList, AdminNewsForm }            from './pages/admin/AdminNews';
import { AdminPublicationsList, AdminPublicationsForm } from './pages/admin/AdminPublications';
import { AdminSliderList, AdminSliderForm }        from './pages/admin/AdminSlider';
import AdminSettings        from './pages/admin/AdminSettings';
import AdminDepartments     from './pages/admin/AdminDepartments';
import AdminDepartmentForm  from './pages/admin/AdminDepartmentForm';
import AdminLeadership      from './pages/admin/AdminLeadership';
import AdminLeadershipForm  from './pages/admin/AdminLeadershipForm';
import AdminPages           from './pages/admin/AdminPages';
import AdminPageForm        from './pages/admin/AdminPageForm';
import AdminUsers           from './pages/admin/AdminUsers';
import AdminUserForm        from './pages/admin/AdminUserForm';
import AdminServices        from './pages/admin/AdminServices';
import AdminServiceForm     from './pages/admin/AdminServiceForm';
import AdminNav            from './pages/admin/AdminNav';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="spinner" style={{marginTop:'30vh'}}/>;
  if (!user)   return <Navigate to="/admin/login" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user)    return <Navigate to="/admin" replace />;
  return children;
}

const P = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ duration: 3500 }}/>
          <Routes>
            {/* ── PUBLIC ──────────────────────── */}
            <Route path="/"                    element={<Home />} />
            <Route path="/lajmet"              element={<NewsList />} />
            <Route path="/lajmet/:slug"        element={<NewsDetail />} />
            <Route path="/publikimet"          element={<Publications />} />
            <Route path="/departamentet"       element={<SimplePublicPage title="Departamentet" apiPath="/departments" mode="depts"/>} />
            <Route path="/departamentet/:slug" element={<SimplePublicPage title="Departamenti"  apiPath="/departments/:slug" mode="dept-detail"/>} />
            <Route path="/udheheqja"           element={<SimplePublicPage title="Udhëheqja"     apiPath="/leadership?category=udheheqja" mode="leadership"/>} />
            <Route path="/sherbimet"           element={<SimplePublicPage title="Shërbimet"     apiPath="/services" mode="services"/>} />
            <Route path="/historiku"           element={<SimplePublicPage title="Historiku"     apiPath="/pages/historiku" mode="page"/>} />
            <Route path="/per-pacientin"       element={<SimplePublicPage title="Për Pacientin" apiPath="/pages/per-pacientin" mode="page"/>} />
            <Route path="/si-te-vini"          element={<SimplePublicPage title="Si të vini"   apiPath="/pages/si-te-vini" mode="page"/>} />
            <Route path="/kontakti"            element={<Contact />} />
            <Route path="/faqe/:slug"          element={<SimplePublicPage title="" apiPath="/pages/:slug" mode="page"/>} />

            {/* ── ADMIN ───────────────────────── */}
            <Route path="/admin/login" element={<GuestRoute><AdminLogin /></GuestRoute>}/>

            <Route path="/admin"                           element={<P><AdminDashboard /></P>}/>
            <Route path="/admin/slider"                    element={<P><AdminSliderList /></P>}/>
            <Route path="/admin/slider/i-ri"               element={<P><AdminSliderForm /></P>}/>
            <Route path="/admin/slider/:id/ndrysho"        element={<P><AdminSliderForm /></P>}/>
            <Route path="/admin/lajmet"                    element={<P><AdminNewsList /></P>}/>
            <Route path="/admin/lajmet/i-ri"               element={<P><AdminNewsForm /></P>}/>
            <Route path="/admin/lajmet/:id/ndrysho"        element={<P><AdminNewsForm /></P>}/>
            <Route path="/admin/publikimet"                element={<P><AdminPublicationsList /></P>}/>
            <Route path="/admin/publikimet/i-ri"           element={<P><AdminPublicationsForm /></P>}/>
            <Route path="/admin/publikimet/:id/ndrysho"    element={<P><AdminPublicationsForm /></P>}/>
            <Route path="/admin/departamentet"             element={<P><AdminDepartments /></P>}/>
            <Route path="/admin/departamentet/i-ri"        element={<P><AdminDepartmentForm /></P>}/>
            <Route path="/admin/departamentet/:id/ndrysho" element={<P><AdminDepartmentForm /></P>}/>
            <Route path="/admin/sherbimet"                 element={<P><AdminServices /></P>}/>
            <Route path="/admin/sherbimet/i-ri"            element={<P><AdminServiceForm /></P>}/>
            <Route path="/admin/sherbimet/:id/ndrysho"     element={<P><AdminServiceForm /></P>}/>
            <Route path="/admin/udheheqja"                 element={<P><AdminLeadership /></P>}/>
            <Route path="/admin/udheheqja/i-ri"            element={<P><AdminLeadershipForm /></P>}/>
            <Route path="/admin/udheheqja/:id/ndrysho"     element={<P><AdminLeadershipForm /></P>}/>
            <Route path="/admin/faqet"                     element={<P><AdminPages /></P>}/>
            <Route path="/admin/faqet/i-re"                element={<P><AdminPageForm /></P>}/>
            <Route path="/admin/faqet/ndrysho/:slug"       element={<P><AdminPageForm /></P>}/>
            <Route path="/admin/perdoruesit"               element={<P><AdminUsers /></P>}/>
            <Route path="/admin/perdoruesit/i-ri"          element={<P><AdminUserForm /></P>}/>
            <Route path="/admin/perdoruesit/:id/ndrysho"   element={<P><AdminUserForm /></P>}/>
            <Route path="/admin/cilesimet"                 element={<P><AdminSettings /></P>}/>
            <Route path="/admin/menyja"                    element={<P><AdminNav /></P>}/>

            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}
